import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, throwError } from 'rxjs';

import { AuthStore } from '@/core/auth/stores/auth.store';
import { IS_LOGIN_ATTEMPT } from '@/core/auth/login-attempt-context';
import { ToastService } from '@/core/toast/toast.service';
import { NOT_FOUND_IS_DEAD_END } from '@/core/http/not-found-redirect';
import { showNotFound } from '@/core/routing/not-found';
import { LessonAccessService } from '@/shared/content/lesson-access/lesson-access.service';
import {
  ApiError,
  isApiError,
  isProblemDetails,
  toApiError,
} from '@/core/http/problem-details/problem-details';

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const authStore = inject(AuthStore);
  const transloco = inject(TranslocoService);
  const toastService = inject(ToastService);
  const router = inject(Router);
  const lessonAccess = inject(LessonAccessService);

  return next(request).pipe(
    catchError((response: unknown) => {
      // authInterceptor re-enters this chain to refresh, so one failure can reach here twice.
      // Remapping the second pass would undo the screenReplaced the first pass set.
      if (isApiError(response)) {
        return throwError(() => response);
      }
      const apiError = mapToApiError(response, transloco);
      if (apiError.status === 401 && !request.context.get(IS_LOGIN_ATTEMPT)) {
        const hadSession = authStore.accessToken() !== null;
        // A session dies here, never through the logout that AuthFeatureService.endSession owns, so
        // anything scoped to the session has to come down here too or it outlives the visitor.
        authStore.clear();
        lessonAccess.clearRedirect();
        if (hadSession) {
          const fromBack =
            response instanceof HttpErrorResponse && isProblemDetails(response.error);
          toastService.show(
            'danger',
            fromBack ? apiError.detail : transloco.translate('auth.sessionExpired'),
          );
          void router.navigateByUrl('/login');
          apiError.screenReplaced = true;
        }
      }
      // A 404 only replaces the screen when the failed request *is* the resource the URL names —
      // which the caller declares, since only it knows (see NOT_FOUND_IS_DEAD_END). Every other
      // request keeps the page and its own error handling: there the page is valid and the action
      // failed. Mutations never qualify, so they need nothing to stay put.
      if (
        apiError.status === 404 &&
        authStore.isAuthenticated() &&
        request.context.get(NOT_FOUND_IS_DEAD_END)
      ) {
        showNotFound(router);
        apiError.screenReplaced = true;
      }
      return throwError(() => apiError);
    }),
  );
};

function mapToApiError(response: unknown, transloco: TranslocoService): ApiError {
  if (response instanceof HttpErrorResponse) {
    if (isProblemDetails(response.error)) {
      return toApiError(response.error);
    }
    return transportFallback(response.status, transloco);
  }
  return transportFallback(0, transloco);
}

function transportFallback(status: number, transloco: TranslocoService): ApiError {
  return {
    status,
    type: 'about:blank',
    detail: transloco.translate('common.internalError'),
    validationErrors: null,
    extra: {},
    screenReplaced: false,
  };
}
